'use client';

import { useState } from 'react';

export interface PdfPickerItem {
  title: string;
  url: string;       // the PDF (served) url
  cover?: string;    // optional cover image url
}

export interface PdfPickerProps {
  title?: string;
  items: PdfPickerItem[];
  height?: string;   // viewer height, default 760px
}

/**
 * One PDF viewer pane with a selector — a cover slider AND a title dropdown —
 * that swap which document is loaded, instead of stacking N embeds. Driven by
 * variables.json (pageData.pdfPicker), rendered by Body, mirroring how pageData
 * .pdfs[] drives <PDFViewer>. A client component because the page content itself
 * is static HTML (inline scripts are stripped), so interactivity must live here.
 */
export default function PdfPicker({ title, items, height = '760px' }: PdfPickerProps) {
  const [active, setActive] = useState(0);
  if (!items?.length) return null;
  const cur = items[Math.min(active, items.length - 1)];

  return (
    <section style={{ margin: '1.5rem 0' }}>
      {title && <h2 style={{ margin: '0 0 .75rem' }}>{title}</h2>}

      {/* dropdown + open-in-new-tab */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', flexWrap: 'wrap', marginBottom: '.6rem' }}>
        <label style={{ fontSize: '.85rem', color: '#444' }}>
          Document:{' '}
          <select
            value={active}
            onChange={(e) => setActive(Number(e.target.value))}
            style={{ padding: '.3rem .5rem', maxWidth: '20rem' }}
          >
            {items.map((it, i) => (
              <option key={i} value={i}>{it.title}</option>
            ))}
          </select>
        </label>
        <a href={cur.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '.8rem', color: '#2563eb' }}>
          ↗ open “{cur.title}”
        </a>
      </div>

      {/* cover slider */}
      <div
        role="tablist"
        aria-label="Choose a document"
        style={{ display: 'flex', flexWrap: 'nowrap', gap: '.6rem', overflowX: 'auto', padding: '0 0 .5rem', WebkitOverflowScrolling: 'touch' }}
      >
        {items.map((it, i) => {
          const selected = i === active;
          return (
            <button
              key={i}
              role="tab"
              aria-selected={selected}
              title={it.title}
              onClick={() => setActive(i)}
              style={{
                flex: '0 0 auto', cursor: 'pointer', padding: 0, background: 'none',
                border: selected ? '2px solid #2563eb' : '2px solid transparent',
                borderRadius: 6, lineHeight: 0,
              }}
            >
              {it.cover ? (
                <img src={it.cover} alt={it.title} loading="lazy"
                  style={{ width: 92, height: 119, objectFit: 'cover', display: 'block', borderRadius: 4 }} />
              ) : (
                <span style={{
                  display: 'flex', width: 92, height: 119, alignItems: 'center', justifyContent: 'center',
                  background: '#1f2937', color: '#e5e7eb', fontSize: '.62rem', textAlign: 'center', padding: 4, borderRadius: 4,
                }}>{it.title}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* the single viewer — keyed on url so changing selection cleanly reloads */}
      <iframe
        key={cur.url}
        src={cur.url}
        title={cur.title}
        width="100%"
        height={height}
        style={{ border: '1px solid #333', borderRadius: 8, display: 'block', marginTop: '.5rem' }}
        loading="lazy"
        allowFullScreen
      />
    </section>
  );
}
